import type { FC, FormEvent, ReactNode } from 'react'
import type { AssistantReply, ChatAction, ChatMessage } from '@/types'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

import {
    ArrowRight,
    Instagram,
    MessageCircleHeart,
    Navigation,
    Phone,
    SendHorizontal,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { whatsappUrl } from '@/lib/business'
import { ASSISTANT_GREETING, getAssistantReply } from '@/lib/chat-assistant'
import { WhatsAppIcon } from '@/components/generals/icons'

const MAX_LENGTH = 500

const isSmallScreen = (): boolean => window.matchMedia('(max-width: 767px)').matches

interface ActionButtonProps {
    action: ChatAction
    onNavigate: () => void
}

const actionClass = 'inline-flex min-h-9 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-[filter,background-color] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'

const ActionButton: FC<ActionButtonProps> = ({ action, onNavigate }): ReactNode => {
    if (action.kind === 'link') {
        return (
            <Link href={action.href} onClick={onNavigate} className={cn(actionClass, 'border border-gold/50 bg-accent/50 text-plum hover:bg-accent')}>
                {action.label}
                <ArrowRight className='size-3.5' aria-hidden='true' />
            </Link>
        )
    }

    if (action.kind === 'call') {
        return (
            <a href={action.href} className={cn(actionClass, 'bg-plum text-ivory hover:brightness-125')}>
                <Phone className='size-3.5' aria-hidden='true' />
                {action.label}
            </a>
        )
    }

    const styles: Record<string, string> = {
        whatsapp: 'bg-[#1fae53] text-white hover:brightness-110',
        directions: 'border border-gold/50 bg-white text-plum hover:bg-accent/60',
        instagram: 'border border-gold/50 bg-white text-plum hover:bg-accent/60'
    }

    return (
        <a
            href={action.href}
            target='_blank'
            rel='noopener noreferrer'
            className={cn(actionClass, styles[action.kind])}
        >
            {action.kind === 'whatsapp' && <WhatsAppIcon className='size-3.5' />}
            {action.kind === 'directions' && <Navigation className='size-3.5' aria-hidden='true' />}
            {action.kind === 'instagram' && <Instagram className='size-3.5' aria-hidden='true' />}
            {action.label}
        </a>
    )
}

export const ChatWidget: FC = (): ReactNode => {
    const [isOpen, setIsOpen] = useState(false)
    const [hasOpened, setHasOpened] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [input, setInput] = useState('')
    const [messages, setMessages] = useState<ChatMessage[]>([
        { id: 'welcome', role: 'assistant', ...ASSISTANT_GREETING }
    ])

    const idRef = useRef(0)
    const busyRef = useRef(false)
    const restoreFocusRef = useRef(false)
    const listRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)

    const nextId = (): string => {
        idRef.current += 1
        return `message-${idRef.current}`
    }

    useEffect(() => {
        const list = listRef.current

        if (list) list.scrollTop = list.scrollHeight
    }, [messages, isTyping, isOpen])

    useEffect(() => {
        if (!isOpen) {
            if (restoreFocusRef.current) {
                restoreFocusRef.current = false
                toggleRef.current?.focus()
            }

            return
        }

        if (!isSmallScreen()) inputRef.current?.focus()

        const previousOverflow = document.body.style.overflow

        if (isSmallScreen()) document.body.style.overflow = 'hidden'

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            restoreFocusRef.current = true
            setIsOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = previousOverflow
        }
    }, [isOpen])

    const openChat = () => {
        setIsOpen(true)
        setHasOpened(true)
    }

    const closeChat = () => {
        restoreFocusRef.current = true
        setIsOpen(false)
    }

    const closeOnSmallScreen = () => {
        if (isSmallScreen()) setIsOpen(false)
    }

    const send = async (text: string) => {
        const content = text.trim().slice(0, MAX_LENGTH)

        if (!content || busyRef.current) return

        busyRef.current = true

        const userMessage: ChatMessage = { id: nextId(), role: 'user', text: content }
        const history = [...messages, userMessage]

        setMessages(history)
        setInput('')
        setIsTyping(true)

        let reply: AssistantReply

        try {
            const response = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: history.slice(-12).map(message => ({ role: message.role, content: message.text }))
                })
            })

            const data = await response.json() as Partial<AssistantReply>

            reply = response.ok && typeof data.text === 'string'
                ? { text: data.text, actions: data.actions, suggestions: data.suggestions }
                : getAssistantReply(content)
        }
        catch {
            reply = getAssistantReply(content)
        }

        setMessages(current => [...current, { id: nextId(), role: 'assistant', ...reply }])
        setIsTyping(false)
        busyRef.current = false
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        void send(input)
    }

    const lastMessage = messages[messages.length - 1]
    const suggestions = !isTyping && lastMessage?.role === 'assistant' ? lastMessage.suggestions ?? [] : []

    return (
        <>
            {!isOpen && (
                <a
                    href={whatsappUrl()}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label='Chat with Anushka Beauty Parlour on WhatsApp'
                    className='fixed bottom-[6.5rem] right-6 z-40 hidden size-14 items-center justify-center rounded-full bg-[#1fae53] text-white shadow-lg shadow-black/25 transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold md:inline-flex'
                >
                    <WhatsAppIcon className='size-7' />
                </a>
            )}

            {!isOpen && !hasOpened && (
                <button
                    type='button'
                    onClick={openChat}
                    className='fixed bottom-9 right-24 z-40 hidden items-center gap-2 rounded-full border border-gold/40 bg-white px-4 py-2 text-sm font-medium text-plum shadow-lg shadow-black/10 animate-in fade-in slide-in-from-right-4 duration-500 lg:inline-flex'
                >
                    Hi! Ask me anything
                </button>
            )}

            <button
                ref={toggleRef}
                type='button'
                onClick={isOpen ? closeChat : openChat}
                aria-expanded={isOpen}
                aria-controls='chat-panel'
                aria-label={isOpen ? 'Close the beauty assistant' : 'Chat with our beauty assistant'}
                className={cn(
                    'gold-gradient fixed bottom-[calc(5.25rem_+_env(safe-area-inset-bottom))] right-4 z-40 inline-flex size-14 items-center justify-center rounded-full text-plum-deep shadow-xl shadow-black/30 ring-4 ring-white/70 transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold md:bottom-6 md:right-6',
                    isOpen && 'hidden md:inline-flex'
                )}
            >
                {isOpen ? <X className='size-6' aria-hidden='true' /> : <MessageCircleHeart className='size-6' aria-hidden='true' />}

                {!hasOpened && (
                    <span className='absolute -right-0.5 -top-0.5 flex size-4' aria-hidden='true'>
                        <span className='absolute inline-flex size-full animate-ping rounded-full bg-plum opacity-60 motion-reduce:hidden' />
                        <span className='relative inline-flex size-4 rounded-full border-2 border-white bg-plum' />
                    </span>
                )}
            </button>

            {isOpen && (
                <section
                    id='chat-panel'
                    role='dialog'
                    aria-labelledby='chat-title'
                    className='animate-in fade-in slide-in-from-bottom-4 fixed inset-0 z-50 flex h-dvh flex-col overflow-hidden bg-white duration-200 md:inset-auto md:bottom-24 md:right-6 md:h-[min(600px,calc(100dvh_-_8rem))] md:w-[390px] md:rounded-3xl md:border md:border-gold/30 md:shadow-2xl md:shadow-black/30'
                >
                    <div className='plum-glow flex items-center gap-3 px-4 pb-3.5 pt-[max(0.875rem,env(safe-area-inset-top))] text-ivory'>
                        <span className='gold-gradient flex size-11 shrink-0 items-center justify-center rounded-full font-heading text-lg font-bold text-plum-deep' aria-hidden='true'>
                            A
                        </span>

                        <div className='min-w-0 flex-1'>
                            <h2 id='chat-title' className='font-heading text-sm font-semibold tracking-wider text-gold-light'>
                                Anushka Assistant
                            </h2>

                            <p className='mt-0.5 flex items-center gap-1.5 text-xs text-ivory/70'>
                                <span className='size-1.5 rounded-full bg-emerald-400' aria-hidden='true' />
                                Replies instantly, any time
                            </p>
                        </div>

                        <a
                            href={whatsappUrl()}
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label='Continue on WhatsApp'
                            className='inline-flex size-10 items-center justify-center rounded-full border border-gold/30 text-gold-light transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-gold'
                        >
                            <WhatsAppIcon className='size-5' />
                        </a>

                        <button
                            type='button'
                            onClick={closeChat}
                            aria-label='Close chat'
                            className='inline-flex size-10 items-center justify-center rounded-full border border-gold/30 text-gold-light transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-gold'
                        >
                            <X className='size-5' aria-hidden='true' />
                        </button>
                    </div>

                    <div
                        ref={listRef}
                        className='flex-1 space-y-4 overflow-y-auto overscroll-contain bg-ivory px-4 py-5'
                        aria-live='polite'
                        aria-relevant='additions'
                    >
                        {messages.map(message => (
                            <div
                                key={message.id}
                                className={cn('flex gap-2', message.role === 'user' ? 'justify-end' : 'justify-start')}
                            >
                                {message.role === 'assistant' && (
                                    <span className='gold-gradient mt-1 flex size-7 shrink-0 items-center justify-center rounded-full font-heading text-xs font-bold text-plum-deep' aria-hidden='true'>
                                        A
                                    </span>
                                )}

                                <div className={cn('flex max-w-[85%] flex-col gap-2', message.role === 'user' && 'items-end')}>
                                    <p
                                        className={cn(
                                            'whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                                            message.role === 'user'
                                                ? 'rounded-br-md bg-plum text-ivory'
                                                : 'rounded-bl-md border border-border bg-white text-foreground shadow-sm'
                                        )}
                                    >
                                        <span className='sr-only'>{message.role === 'user' ? 'You said: ' : 'Assistant: '}</span>
                                        {message.text}
                                    </p>

                                    {message.actions && message.actions.length > 0 && (
                                        <div className='flex flex-wrap gap-2'>
                                            {message.actions.map(action => (
                                                <ActionButton key={`${message.id}-${action.href}`} action={action} onNavigate={closeOnSmallScreen} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className='flex items-center gap-2'>
                                <span className='gold-gradient flex size-7 items-center justify-center rounded-full font-heading text-xs font-bold text-plum-deep' aria-hidden='true'>
                                    A
                                </span>

                                <span className='flex items-center gap-1 rounded-2xl rounded-bl-md border border-border bg-white px-4 py-3.5 shadow-sm'>
                                    <span className='sr-only'>The assistant is typing</span>
                                    <span className='size-1.5 animate-bounce rounded-full bg-gold-dark [animation-delay:-0.3s]' aria-hidden='true' />
                                    <span className='size-1.5 animate-bounce rounded-full bg-gold-dark [animation-delay:-0.15s]' aria-hidden='true' />
                                    <span className='size-1.5 animate-bounce rounded-full bg-gold-dark' aria-hidden='true' />
                                </span>
                            </div>
                        )}

                        {suggestions.length > 0 && (
                            <div className='flex flex-wrap gap-2 pl-9'>
                                {suggestions.map(suggestion => (
                                    <button
                                        key={suggestion}
                                        type='button'
                                        onClick={() => void send(suggestion)}
                                        className='min-h-9 rounded-full border border-gold/45 bg-white px-3.5 py-1.5 text-xs font-medium text-plum transition-colors hover:border-gold hover:bg-accent/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={onSubmit}
                        className='border-t border-border bg-white px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3'
                    >
                        <div className='flex items-center gap-2'>
                            <label htmlFor='chat-input' className='sr-only'>
                                Type your question
                            </label>

                            <input
                                id='chat-input'
                                ref={inputRef}
                                value={input}
                                onChange={event => setInput(event.target.value)}
                                maxLength={MAX_LENGTH}
                                placeholder='Ask about services, timings, booking...'
                                autoComplete='off'
                                enterKeyHint='send'
                                className='h-12 min-w-0 flex-1 rounded-full border border-input bg-ivory px-4 text-base text-foreground placeholder:text-muted-foreground/80 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40 md:h-11 md:text-sm'
                            />

                            <button
                                type='submit'
                                disabled={!input.trim() || isTyping}
                                aria-label='Send message'
                                className='inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-plum text-ivory transition-[filter,opacity] hover:brightness-125 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold md:size-11'
                            >
                                <SendHorizontal className='size-4' aria-hidden='true' />
                            </button>
                        </div>

                        <p className='mt-2 text-center text-[0.7rem] text-muted-foreground'>
                            Automated assistant. To book, chat with us on WhatsApp.
                        </p>
                    </form>
                </section>
            )}
        </>
    )
}
